/**
 * BrandEquityCard - Standalone component for displaying brand equity overview
 * 
 * Extracted from ExecutiveDashboard.tsx for reusability.
 * 
 * NOTE: This component is NOT YET INTEGRATED. Created for review before use.
 * To integrate, import and use with the required props from useBrandMetrics hook.
 * 
 * Example usage:
 * ```tsx
 * import BrandEquityCard from '../components/BrandEquityCard';
 * 
 * const { metrics, digitalMetrics, healthIndex, healthIndexLoading } = useBrandMetrics();
 * 
 * <BrandEquityCard
 *   metrics={metrics}
 *   digitalMetrics={digitalMetrics}
 *   healthIndex={healthIndex}
 *   loading={healthIndexLoading}
 *   showNewBadge={isDemoMode}
 * />
 * ```
 */

import { useMemo } from "react";
import { isDemoMode } from "../services";
import type { BrandMetricsResponse, FullDigitalData, HealthResponse } from "../config/api/types.gen";

interface BrandEquityCardProps {
  /** Brand metrics data including traditional metrics */
  metrics: BrandMetricsResponse | null | undefined;
  /** Digital metrics data */
  digitalMetrics: FullDigitalData | null | undefined;
  /** Health index data including percentile and rank */
  health: HealthResponse | null | undefined;
  /** Loading state for the health index */
  loading?: boolean;
  /** Whether to show the "NEW" badge (defaults to isDemoMode) */
  showNewBadge?: boolean;
  /** Additional CSS classes for the container */
  className?: string;
  /** Card title (defaults to "Brand Equity Overview") */
  title?: string;
}

/** Number formatting helper - matches ExecutiveDashboard formatting */
const formatNumber = (value: number | null | undefined): string => {
  return (value ?? 0).toLocaleString('en-US', { maximumFractionDigits: 0 });
};

/**
 * Calculates traditional brand equity from core metrics
 */
function calculateTraditionalEquity(metrics: BrandMetricsResponse['metrics']|null): number {
  if (!metrics) return 0;
  return (metrics.awareness + metrics.trust + metrics.quality + metrics.loyalty) / 4;
}

/**
 * Calculates total brand equity combining traditional and digital
 */
function calculateTotalEquity(
  metrics: BrandMetricsResponse['metrics']|null,
  digitalMetrics: FullDigitalData | null | undefined
): number {
  if (!metrics) return 0;
  const traditionalEquity = calculateTraditionalEquity(metrics);
  const digitalEquity = digitalMetrics?.sentiment_score || 70;
  return traditionalEquity * 0.6 + digitalEquity * 0.4;
}

export default function BrandEquityCard({
  health,
  loading = false,
  showNewBadge = isDemoMode,
  className = "",
  title = "Brand Equity Overview",
}: BrandEquityCardProps) {
  // ============================================================
  // Computed values - all displayed data extracted for easy editing
  // ============================================================
  const displayValues = useMemo(() => {
    return {
      // Hero metrics
      healthIndex: formatNumber(health?.health_index),
      totalEquity: formatNumber(health?.equity.total_equity),

      // Equity breakdown
      traditionalEquity: formatNumber(health?.equity.trad_equity),
      traditionalEquityPercent: health?.equity.trad_equity, // For progress bar width
      digitalEquity: formatNumber(health?.equity.digital_equity),
      digitalEquityPercent: health?.equity.digital_equity, // For progress bar width

      // Key performance indicators
      percentile: formatNumber(health?.percentile),
      rank: health?.rank || 0,
      shareOfVoice: formatNumber(health?.digital?.share_of_voice),
    };
  }, [health]);

  return (
    <div className={`bg-card rounded-2xl p-6 border border-[var(--border)] relative overflow-hidden ${className}`}>
      {/* New Badge */}
      {showNewBadge && (
        <>
          <div
            className="absolute top-0 right-0 text-white text-sm font-bold px-4 py-1.5 rounded-bl-xl shadow-lg"
            style={{
              animation: 'flashColors 1s ease-in-out infinite',
              background: 'linear-gradient(90deg, #10b981, #facc15)'
            }}
          >
            ✨ NEW
          </div>
          <style>{`
            @keyframes flashColors {
              0%, 100% { background: linear-gradient(90deg, #10b981, #22c55e); }
              50% { background: linear-gradient(90deg, #facc15, #eab308); }
            }
          `}</style>
        </>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl font-semibold text-foreground">{title}</h3>
        <i className="fas fa-heartbeat text-secondary text-2xl"></i>
      </div>

      {/* Content */}
      {!loading ? (
        <div>
          {/* Hero Metrics Row */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            {/* Brand Health Index */}
            <div className="text-center p-3 bg-background-tertiary/50 rounded-xl">
              <p className="text-xs text-text-muted mb-1">Brand Health Index</p>
              <div className="text-3xl font-bold bg-gradient-primary text-transparent bg-clip-text">
                {displayValues.healthIndex}
              </div>
              <p className="text-xs text-text-muted">vs Avg (100)</p>
            </div>
            {/* Total Brand Equity */}
            <div className="text-center p-3 bg-background-tertiary/50 rounded-xl">
              <p className="text-xs text-text-muted mb-1">Total Brand Equity</p>
              <div className="text-3xl font-bold text-purple-500">
                {displayValues.totalEquity}
              </div>
              <p className="text-xs text-text-muted">Combined Score</p>
            </div>
          </div>

          {/* Equity Breakdown */}
          <div className="space-y-3 mb-4">
            {/* Traditional Brand Equity */}
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-text-muted">Traditional Equity</span>
                <span className="font-semibold text-green-400">
                  {displayValues.traditionalEquity}%
                </span>
              </div>
              <div className="w-full bg-background-tertiary rounded-full h-2">
                <div
                  className="bg-green-500 h-2 rounded-full transition-all duration-500"
                  style={{ width: `${displayValues.traditionalEquityPercent}%` }}
                ></div>
              </div>
            </div>
            {/* Digital Brand Equity */}
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-text-muted">Digital Equity</span>
                <span className="font-semibold text-cyan-400">
                  {displayValues.digitalEquity}%
                </span>
              </div>
              <div className="w-full bg-background-tertiary rounded-full h-2">
                <div
                  className="bg-cyan-500 h-2 rounded-full transition-all duration-500"
                  style={{ width: `${displayValues.digitalEquityPercent}%` }}
                ></div>
              </div>
            </div>
          </div>

          {/* Key Performance Indicators */}
          <div className="pt-3 border-t border-[var(--border)]">
            <div className="grid grid-cols-3 gap-2 text-center">
              <div>
                <p className="text-lg font-bold text-foreground">
                  {displayValues.percentile}th
                </p>
                <p className="text-xs text-text-muted">Percentile</p>
              </div>
              <div>
                <p className="text-lg font-bold text-foreground">
                  #{displayValues.rank}
                </p>
                <p className="text-xs text-text-muted">Category Rank</p>
              </div>
              <div>
                <p className="text-lg font-bold text-foreground">
                  {displayValues.shareOfVoice}%
                </p>
                <p className="text-xs text-text-muted">Share of Voice</p>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      )}
    </div>
  );
}

// Named exports for utility functions (can be used independently)
export { calculateTraditionalEquity, calculateTotalEquity, formatNumber };
export type { BrandEquityCardProps };
